/**
 * @file NotificationTemplatesPage.tsx
 * @description Управление шаблонами уведомлений магазина (phase10.5 шаг 4).
 *
 * Под SHOP_MANAGE_SETTINGS. Master-detail UI:
 *  - Слева: список kinds × channels с фильтрами.
 *  - При клике — inline-редактор справа (subject / body).
 *  - Системные шаблоны защищены от удаления, но можно деактивировать
 *    или сохранить кастомную версию поверх.
 *
 * Mobile-first: на `< md` список и редактор переключаются (full-screen).
 * Drawer / Tabs — на следующей итерации; на сейчас простой stacked layout.
 */

import { Bell, Eye, Loader2, Mail, MessageSquare, Save, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Label } from "../../../components/ui/label";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import { ApiError } from "../../../lib/api-client";
import {
  listNotificationTemplates,
  NOTIFICATION_CHANNEL_LABELS,
  NOTIFICATION_KIND_LABELS,
  previewNotificationTemplate,
  removeNotificationTemplate,
  TRANSACTIONAL_KINDS,
  updateNotificationTemplate,
  type NotificationChannel,
  type NotificationKind,
  type NotificationTemplateDto,
} from "../../../lib/api-notification-templates";

type ChannelFilter = "all" | NotificationChannel;

export function NotificationTemplatesPage() {
  useParams<{ slug: string }>();
  const notify = useNotify();
  const [items, setItems] = useState<NotificationTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formSubject, setFormSubject] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formActive, setFormActive] = useState(true);
  // phase19 — превью с тестовыми данными (рендер несохранённых правок).
  const [preview, setPreview] = useState<{ subject: string | null; body: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const selected = useMemo(
    () => items.find((it) => it.id === selectedId) ?? null,
    [items, selectedId],
  );

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await listNotificationTemplates(
        channelFilter === "all" ? undefined : { channel: channelFilter },
      );
      setItems(res.items);
      const first = res.items[0];
      if (!selectedId && first) {
        setSelectedId(first.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelFilter]);

  // При смене выбранного — синхронизируем форму (и сбрасываем превью).
  useEffect(() => {
    if (selected) {
      setFormSubject(selected.subject ?? "");
      setFormBody(selected.body);
      setFormActive(selected.isActive);
      setPreview(null);
    }
  }, [selected]);

  /** phase19 — рендер письма с тестовыми данными (что видит клиент). */
  async function onPreview(): Promise<void> {
    if (!selected) return;
    setPreviewing(true);
    try {
      const rendered = await previewNotificationTemplate({
        subject:
          selected.channel === "SMS" || selected.channel === "TELEGRAM"
            ? undefined
            : formSubject.trim() || undefined,
        body: formBody,
      });
      setPreview(rendered);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Ошибка превью");
    } finally {
      setPreviewing(false);
    }
  }

  async function onSave(): Promise<void> {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await updateNotificationTemplate(selected.id, {
        subject: selected.channel === "SMS" || selected.channel === "TELEGRAM"
          ? null
          : formSubject.trim() || null,
        body: formBody,
        isActive: formActive,
      });
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      notify.success("Шаблон сохранён");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  }

  // NB: «клонирование» системного шаблона не нужно — кнопка «Сохранить» уже
  // обновляет body/subject/isActive системного шаблона (см. validation в
  // notification-templates.service: системный нельзя удалить, но содержимое
  // редактируется). Если в будущем понадобится «отвязать кастомную копию» —
  // менять @@unique([shopId, kind, channel, locale]) (добавить isSystem)
  // или вводить деактивацию системного. Это отдельная фаза.

  async function onDelete(): Promise<void> {
    if (!selected || selected.isSystem) return;
    if (!window.confirm(`Удалить шаблон «${NOTIFICATION_KIND_LABELS[selected.kind]} × ${NOTIFICATION_CHANNEL_LABELS[selected.channel]}»?`)) return;
    setDeleting(true);
    try {
      await removeNotificationTemplate(selected.id);
      notify.success("Шаблон удалён");
      setSelectedId(null);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Ошибка удаления");
    } finally {
      setDeleting(false);
    }
  }

  // Группировка для отображения списка: по kind, внутри по channel.
  const groupedItems = useMemo(() => {
    const byKind = new Map<NotificationKind, NotificationTemplateDto[]>();
    for (const it of items) {
      const list = byKind.get(it.kind) ?? [];
      list.push(it);
      byKind.set(it.kind, list);
    }
    return Array.from(byKind.entries());
  }, [items]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Настройки · Уведомления</div>
        <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-semibold tracking-tight">
            Шаблоны уведомлений
          </h1>
        </div>
        <p className="text-sm text-on-surface-variant mt-1">
          Тексты email / SMS / Telegram-сообщений, которые отправляются клиентам
          при событиях заказа. Используйте Handlebars-переменные:
          <code className="ml-1 font-mono text-xs">{`{{customer.displayName}}`}</code>,
          <code className="ml-1 font-mono text-xs">{`{{order.number}}`}</code>,
          <code className="ml-1 font-mono text-xs">{`{{shop.displayName}}`}</code>.
        </p>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-on-surface-variant mr-2">Канал:</span>
            <Button
              variant={channelFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setChannelFilter("all")}
            >
              Все
            </Button>
            <Button
              variant={channelFilter === "EMAIL" ? "default" : "outline"}
              size="sm"
              onClick={() => setChannelFilter("EMAIL")}
            >
              <Mail className="size-4 mr-1" /> Email
            </Button>
            <Button
              variant={channelFilter === "SMS" ? "default" : "outline"}
              size="sm"
              onClick={() => setChannelFilter("SMS")}
            >
              <MessageSquare className="size-4 mr-1" /> SMS
            </Button>
            <Button
              variant={channelFilter === "TELEGRAM" ? "default" : "outline"}
              size="sm"
              onClick={() => setChannelFilter("TELEGRAM")}
            >
              <Send className="size-4 mr-1" /> Telegram
            </Button>
          </div>
        </EditorialCard.Body>
      </EditorialCard>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-on-surface-variant" />
        </div>
      ) : items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body>
            <div className="flex flex-col items-center text-center py-12 text-on-surface-variant gap-3">
              <Bell className="size-10 opacity-50" />
              <div>Шаблонов нет. Запустите seed системных шаблонов.</div>
              <code className="font-mono text-xs">
                pnpm --filter=@chaiberi/api notifications:seed
              </code>
            </div>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[minmax(280px,360px)_1fr] gap-4">
          {/* Список шаблонов */}
          <div className="flex flex-col gap-2">
            {groupedItems.map(([kind, kindItems]) => (
              <EditorialCard key={kind}>
                <EditorialCard.Body className="!p-3">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-sm font-medium">
                      {NOTIFICATION_KIND_LABELS[kind]}
                    </span>
                    {!TRANSACTIONAL_KINDS.has(kind) ? (
                      <Badge variant="outline" className="text-xs">
                        требует consent
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1">
                    {kindItems.map((it) => (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setSelectedId(it.id)}
                        className={[
                          // Тач-цель ≥ 48 до 1024 (QA-5 D15: было 38px на 360).
                          "flex min-h-12 items-center justify-between gap-2 text-left px-2 py-1.5 rounded text-sm transition-colors lg:min-h-10",
                          selectedId === it.id
                            ? "bg-primary/10 text-primary border border-primary/30"
                            : "hover:bg-on-surface/5 border border-transparent",
                        ].join(" ")}
                      >
                        <span className="flex items-center gap-2 truncate">
                          {it.channel === "EMAIL" ? <Mail className="size-3.5" /> : null}
                          {it.channel === "SMS" ? <MessageSquare className="size-3.5" /> : null}
                          {it.channel === "TELEGRAM" ? <Send className="size-3.5" /> : null}
                          {it.channel === "IN_APP" ? <Bell className="size-3.5" /> : null}
                          {NOTIFICATION_CHANNEL_LABELS[it.channel]}
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          {it.isSystem ? (
                            <Badge variant="outline" className="text-[10px] py-0">
                              system
                            </Badge>
                          ) : null}
                          {!it.isActive ? (
                            <Badge variant="outline" className="text-[10px] py-0 opacity-60">
                              off
                            </Badge>
                          ) : null}
                        </span>
                      </button>
                    ))}
                  </div>
                </EditorialCard.Body>
              </EditorialCard>
            ))}
          </div>

          {/* Редактор */}
          <div>
            {selected ? (
              <EditorialCard>
                <EditorialCard.Body className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm text-on-surface-variant">
                        {NOTIFICATION_KIND_LABELS[selected.kind]} ·{" "}
                        {NOTIFICATION_CHANNEL_LABELS[selected.channel]} ·{" "}
                        {selected.locale}
                      </div>
                      <h2 className="text-xl font-semibold tracking-tight mt-1">
                        Редактирование шаблона
                      </h2>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {selected.isSystem ? (
                        <Badge variant="outline">Системный</Badge>
                      ) : (
                        <Badge variant="secondary">Кастомный</Badge>
                      )}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formActive}
                          onCheckedChange={(v) => setFormActive(Boolean(v))}
                        />
                        <span className="text-sm">
                          {formActive ? "Активен" : "Отключён"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(selected.channel === "EMAIL" || selected.channel === "IN_APP") ? (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="subject">Заголовок (subject)</Label>
                      <Input
                        id="subject"
                        value={formSubject}
                        onChange={(e) => setFormSubject(e.target.value)}
                        placeholder="Например: Ваш заказ {{order.number}} принят"
                      />
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="body">Тело сообщения</Label>
                    <Textarea
                      id="body"
                      value={formBody}
                      onChange={(e) => setFormBody(e.target.value)}
                      rows={selected.channel === "SMS" ? 4 : 14}
                      className="font-mono text-sm"
                    />
                    {selected.channel === "SMS" ? (
                      <p className="text-xs text-on-surface-variant">
                        Длина: {formBody.length} символов (одна SMS ≈ 70 для
                        кириллицы).
                      </p>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={onSave} disabled={saving}>
                      {saving ? (
                        <Loader2 className="size-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="size-4 mr-1" />
                      )}
                      Сохранить
                    </Button>
                    <Button variant="outline" onClick={() => void onPreview()} disabled={previewing}>
                      {previewing ? (
                        <Loader2 className="size-4 mr-1 animate-spin" />
                      ) : (
                        <Eye className="size-4 mr-1" />
                      )}
                      Превью
                    </Button>
                    {!selected.isSystem ? (
                      <Button
                        variant="ghost"
                        onClick={onDelete}
                        disabled={deleting}
                      >
                        {deleting ? (
                          <Loader2 className="size-4 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="size-4 mr-1" />
                        )}
                        Удалить
                      </Button>
                    ) : null}
                  </div>

                  {/* phase19 — превью письма с тестовыми данными. */}
                  {preview && (
                    <div className="rounded-lg border border-on-surface/15 bg-surface-lowest p-4">
                      <div className="label-editorial mb-2">
                        Превью · тестовые данные
                      </div>
                      {preview.subject && (
                        <div className="text-sm font-semibold mb-2">
                          {preview.subject}
                        </div>
                      )}
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {preview.body}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-on-surface-variant border-t border-on-surface/8 pt-3">
                    Системный шаблон — нельзя удалить, но можно редактировать
                    тело и отключить. Удалите кастомный шаблон, чтобы вернуться
                    к системному по умолчанию.
                  </div>
                </EditorialCard.Body>
              </EditorialCard>
            ) : (
              <EditorialCard>
                <EditorialCard.Body className="text-center py-12 text-on-surface-variant">
                  Выберите шаблон в списке слева для редактирования.
                </EditorialCard.Body>
              </EditorialCard>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
